import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
import { useEffect, useState } from 'react';
import styles from './filters.module.scss';
import { useSearchParams } from 'next/navigation';
import { safelyParseJSON } from '@/app/helpers/searchparams';
import { FilterOptionValue } from '@mybestbrands/types';

interface Props {
  price: FilterOptionValue | undefined;
  onFilterUpdate: (value: string[] | number[] | number) => void;
}

export default function PriceFilterSlider({ price, onFilterUpdate }: Props) {
  const searchParams = useSearchParams();

  const [lowerBound, upperBound] = price?.options ?? [0, 0];

  const [value, setValue] = useState([lowerBound, upperBound]);

  const [min, max] = value;

  useEffect(() => {
    //We are using effect to set sliders either from
    // facets or if they are already selected from searchparams
    if (searchParams) {
      const filters = searchParams.get('filters');
      const filtersParsed = safelyParseJSON(filters);

      if (filtersParsed.price) setValue(filtersParsed.price);
    } else {
      if (price) setValue(price.options);
    }
  }, [price]);

  function onSliderChange(value: number[] | number) {
    setValue(value as number[]);
  }

  function onMinInputBlur(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = +e.target.value;

    //If user have not provided any value to
    //the input we should immediately return
    if (inputValue === 0) return;

    let min = inputValue || lowerBound;

    //If user enter min lower than facet min
    // we put to facet min!!
    if (min < lowerBound) min = lowerBound;

    e.target.value = '';
    if (min === 0 || min >= max) return;

    setValue([min, max]);
    if (min === lowerBound && max === upperBound) return;
    onFilterUpdate([min.toString(), max.toString()]);
  }

  function onMaxInputBlur(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = +e.target.value;

    //If user have not provided any value to
    //the input we should immediately return
    if (inputValue === 0) return;

    const max = inputValue || upperBound;

    e.target.value = '';
    if (max === 0 || max <= min || max > upperBound) return;

    setValue([min, max]);
    if (min === lowerBound && max === upperBound) return;
    onFilterUpdate([min.toString(), max.toString()]);
  }

  function onChangeComplete(value: number[] | number) {
    onFilterUpdate(value);
  }

  return (
    <>
      <div className="p-2">
        <div className="d-flex gap-2">
          <div data-testid="textInputWrapper" className="w-50">
            <label data-testid="textInputLabel">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                data-testid="minimumInput"
                onBlur={onMinInputBlur}
                placeholder={min.toString() + ' €'}
                type="number"
                className={styles.epIYzo}
                defaultValue=""
              />
            </label>
          </div>
          <span className="d-flex aligncenter">-</span>
          <div data-testid="textInputWrapper" className="w-50">
            <label data-testid="textInputLabel">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                data-testid="maximumInput"
                onBlur={onMaxInputBlur}
                placeholder={max.toString() + ' €'}
                type="number"
                className={styles.epIYzo}
                defaultValue=""
              />
            </label>
          </div>
        </div>

        <Slider
          className="mt-4"
          allowCross={false}
          range
          defaultValue={[lowerBound as number, upperBound as number]}
          value={value as number[]}
          min={lowerBound as number}
          max={upperBound as number}
          onChangeComplete={onChangeComplete}
          onChange={onSliderChange}
          styles={{
            // rail: {},
            track: {
              backgroundColor: 'black',
            },
            tracks: {
              backgroundColor: 'black',
            },
            handle: {
              backgroundColor: 'white',
              opacity: 1,
              borderColor: 'grey',
              height: '1.313rem',
              width: '1.313rem',

              top: 0,
              borderRadius: '100%',
              boxShadow: '0 2px 6px #aaa',
              outline: 'none',
              overflow: 'visible',
            },
          }}
        />
      </div>
    </>
  );
}
